/**
 * Street-name input with live Mapbox geocoding suggestions. Selecting a
 * suggestion hands back the parsed street, house number, postal code and
 * city so the caller can fill the rest of an address form in one go.
 *
 * The suggestion list renders through Radix's Popover primitives (same as
 * LessorCombobox) rather than a plain absolutely-positioned `<ul>`: Popover's
 * Content registers as its own DismissableLayer, so Escape closes just the
 * suggestions even when this field sits inside a Dialog/Sheet — a bare div
 * can't do that, since a Dialog's own Escape handling runs in the capture
 * phase on `document`, before this component's (bubble-phase) key handler
 * ever gets a chance to stop it.
 */
import { useEffect, useId, useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Input } from "@/components/ui/input";
import { MAPBOX_TOKEN } from "@/config/mapbox";
import { cn } from "@/lib/utils";
import { hasPlottableCoordinates } from "@/lib/coordinates";

export interface AddressAutocompleteResult {
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  /**
   * Coordinates of the picked address, from the same geocoding response that
   * produced the suggestion. `null` when Mapbox returned no usable point.
   *
   * We were already paying for this lookup and discarding the answer, which is
   * why every property created through the wizard was stored at 0/0 and its
   * room page drew a map in the Atlantic (COL-48 follow-up).
   */
  lat: number | null;
  lng: number | null;
}

interface MapboxContextItem {
  id: string;
  text: string;
}

interface MapboxFeature {
  place_name: string;
  text: string;
  address?: string;
  context?: MapboxContextItem[];
  /** `[lng, lat]` — Mapbox's ordering, not ours. */
  center?: [number, number];
}

type AddressAutocompleteProps = Omit<
  React.ComponentPropsWithoutRef<typeof Input>,
  "value" | "onChange" | "onSelect"
> & {
  value: string;
  onChange: (value: string) => void;
  onSelect: (result: AddressAutocompleteResult) => void;
};

function contextValue(feature: MapboxFeature, prefix: string): string {
  return feature.context?.find((c) => c.id.startsWith(prefix))?.text ?? "";
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  className,
  id,
  disabled,
  readOnly,
  ...rest
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<MapboxFeature[]>([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  // Only a change the user typed should trigger a new search — `value` also
  // changes on initial hydration (existing address loaded into the form) and
  // right after picking a suggestion (parent writes the resolved value back
  // down), and searching again in either case just reopens the dropdown the
  // user didn't ask for.
  const [query, setQuery] = useState<string | null>(null);
  const locked = disabled || readOnly;
  const showSuggestions = !locked && open && suggestions.length > 0;
  const listId = useId();
  const optionId = (i: number) => `${listId}-option-${i}`;

  useEffect(() => {
    if (locked || query == null || query.trim().length < 3 || !MAPBOX_TOKEN) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?autocomplete=true&limit=5&types=address&language=de&access_token=${MAPBOX_TOKEN}`;
        const res = await fetch(url);
        if (cancelled || !res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setSuggestions(data.features ?? []);
        setOpen(true);
        setHighlighted(-1);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, locked]);

  function pick(feature: MapboxFeature) {
    if (locked) return;
    // Mapbox hands back `center` as [lng, lat]; keep the swap here so callers
    // never have to remember it. Guard the pair rather than trusting it — a
    // malformed response should leave the property ungeocoded, not at 0/0.
    const [lng, lat] = feature.center ?? [];
    const plottable = hasPlottableCoordinates(lat, lng);
    onSelect({
      street: feature.text,
      houseNumber: feature.address ?? "",
      postalCode: contextValue(feature, "postcode"),
      city: contextValue(feature, "place"),
      lat: plottable ? lat : null,
      lng: plottable ? lng : null,
    });
    setQuery(null);
    setOpen(false);
    setSuggestions([]);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      pick(suggestions[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <PopoverPrimitive.Root
      open={showSuggestions}
      onOpenChange={(next) => {
        if (!next) setOpen(false);
      }}
    >
      <PopoverPrimitive.Anchor asChild>
        <Input
          id={id}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setQuery(e.target.value);
            // Drop the previous query's results immediately so a stray Enter
            // during the next debounce/fetch can't pick a suggestion that no
            // longer matches what's actually typed.
            setSuggestions([]);
            setHighlighted(-1);
          }}
          onFocus={() => !locked && suggestions.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={className}
          autoComplete="off"
          disabled={disabled}
          readOnly={readOnly}
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            showSuggestions && highlighted >= 0
              ? optionId(highlighted)
              : undefined
          }
          {...rest}
        />
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          // The input, not the popover content, should keep keyboard focus —
          // suggestions are chosen via arrow keys/mouse while still typing.
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="z-50 w-[--radix-popover-trigger-width] max-h-64 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          <ul id={listId} role="listbox">
            {suggestions.map((feature, i) => (
              <li
                key={`${feature.place_name}-${i}`}
                id={optionId(i)}
                role="option"
                aria-selected={i === highlighted}
                className={cn(
                  "cursor-pointer px-3 py-2 text-sm",
                  i === highlighted
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent hover:text-accent-foreground"
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(feature);
                }}
                onMouseEnter={() => setHighlighted(i)}
              >
                {feature.place_name}
              </li>
            ))}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
